import { createSelector } from "@reduxjs/toolkit";
import {
  BagSize,
  CarsAutocompleteResult,
  CarsCancellationPolicyEnum,
  CarShopResponse,
  DateTime,
  ITrackingProperties,
  SelectedCarFromListProperties,
} from "redmond";
import { isEqual } from "lodash-es";
import { isCorpTenant } from "@capone/common";
import { IStoreState } from "../../../../reducers/types";
import {
  getCarAvailabilityEntryPoint,
  getCarAvailabilityEntryPointsAll,
  getViewedCarsListProperties,
} from "../../../availability/reducer";
import { getSelectedAccount } from "../../../rewards/reducer";
import { getExistingCarsAvailabilityRequestParameters } from "../../../search/reducer";
import { config } from "../../../../api/config";

export const getCarShopSelectedVehicle = (state: IStoreState) => state.carShop.selectedVehicle;

export const getExtraInfo = (state: IStoreState): CarShopResponse | null => state.carShop.extraInfo;

export const getCarShopCallState = (state: IStoreState) =>
  state.carShop.carExtraInfoCallState;

export const getCarShopCallError = (state: IStoreState) =>
  state.carShop.carExtraInfoCallError;

export const getSelectedVehiclePolicyCompliance = createSelector(
  getCarShopSelectedVehicle,
  (selectedVehicle) =>
    selectedVehicle && "corporateTravel" in selectedVehicle
      ? selectedVehicle.corporateTravel.policyCompliance
      : undefined
);

export const getSelectedCarFromListProperties = createSelector(
  getViewedCarsListProperties,
  getCarShopSelectedVehicle,
  getSelectedVehiclePolicyCompliance,
  (
    viewedCarsListProperties,
    selectedVehicle,
    selectedVehiclePolicyCompliance
  ): ITrackingProperties<SelectedCarFromListProperties> => {
    const corpEvents = {
      ...(isCorpTenant(config.TENANT) && {
        in_policy: selectedVehiclePolicyCompliance?.isInPolicy ?? true,
      }),
    };
    return {
      properties: {
        ...viewedCarsListProperties.properties,
        vehicle_type: selectedVehicle?.vehicle.vehicleType ?? "",
        total_price: selectedVehicle?.payNow.fiat.value || 0,
        free_cancellation:
          selectedVehicle?.cancellationPolicy.CancellationPolicy ===
          CarsCancellationPolicyEnum.FreeCancellation,
        unlimited_mileage: !!selectedVehicle?.hasUnlimitedMileage,
        automatic: !!selectedVehicle?.vehicle.transmissionType,
        supplier: selectedVehicle?.supplierRef || "",
        vendor: selectedVehicle?.vendorRef || "",
        conditional_cancellation:
          selectedVehicle?.cancellationPolicy.CancellationPolicy ===
          CarsCancellationPolicyEnum.Conditional,
        conditional_cancellation_after_booking:
          selectedVehicle?.cancellationPolicy.CancellationPolicy ===
          CarsCancellationPolicyEnum.ConditionalAfterBooking,
        non_refundable:
          selectedVehicle?.cancellationPolicy.CancellationPolicy ===
          CarsCancellationPolicyEnum.NonRefundable,
        ...corpEvents,
      },
      encryptedProperties: [...viewedCarsListProperties.encryptedProperties],
    };
  }
);

export const getViewedCarDetailsProperties = createSelector(
  getSelectedCarFromListProperties,
  getSelectedAccount,
  getCarShopSelectedVehicle,
  getCarAvailabilityEntryPoint,
  getCarAvailabilityEntryPointsAll,
  (
    selectedCarFromListProperties,
    account,
    carShopSelectedVehicle,
    carAvailabilityEntryPoint,
    carAvailabilityEntryTypesAll
  ): ITrackingProperties => ({
    properties: {
      ...selectedCarFromListProperties.properties,
      account_type_selected: account?.productDisplayName || "",
      fuel_policy: carShopSelectedVehicle?.fuelPolicy?.kind || "",
      ...carShopSelectedVehicle?.offer?.trackingPropertiesV2?.properties,
      has_offer: !!carShopSelectedVehicle?.offer,
      account_use_type: account?.accountUseType,
      account_allow_rewards_redemption: account?.allowRewardsRedemption,
      pax_count: carShopSelectedVehicle?.vehicle.passengerCapacity,
      bags_count:
        carShopSelectedVehicle?.vehicle.bagCapacity[BagSize.Unspecified],
      pickup_location: carShopSelectedVehicle?.pickUp.address,
      car_type: carShopSelectedVehicle?.vehicle.kind,
      same_dropoff: isEqual(
        carShopSelectedVehicle?.pickUp.address,
        carShopSelectedVehicle?.dropOff.address
      ),
      entry_type: carAvailabilityEntryPoint,
      entry_types_all: carAvailabilityEntryTypesAll,
    },
    encryptedProperties: [
      ...selectedCarFromListProperties.encryptedProperties,
      carShopSelectedVehicle?.offer?.trackingPropertiesV2
        ?.encryptedProperties ?? "",
    ],
  })
);

export const getExistingCarsShopRequestParameters = createSelector(
  getExistingCarsAvailabilityRequestParameters,
  getCarShopSelectedVehicle,
  (
    params,
    selectedVehicle
  ): {
    dropOffDate: Date | null;
    dropOffTime: DateTime | null;
    dropOffLocation: CarsAutocompleteResult | null;
    pickUpDate: Date | null;
    pickUpTime: DateTime | null;
    pickUpLocation: CarsAutocompleteResult | null;
    driverAge: number | null;
    vehicleId: string | null;
  } => ({
    vehicleId: selectedVehicle?.id || null,
    dropOffDate: params.dropOffDate,
    dropOffTime: params.dropOffTime,
    dropOffLocation: params.dropOffLocation,
    pickUpDate: params.pickUpDate,
    pickUpTime: params.pickUpTime,
    pickUpLocation: params.pickUpLocation,
    driverAge: params.driverAge,
  })
);
